import { createTheme } from '@mui/material/styles'

declare module '@mui/material/styles' {
  interface Theme {
    lennarDesignSystem: {
      lennarBlues: {
        lightBlue4: string
        lennarBlue: string
        oceanBlue: string
        darkBlue: string
      }
      lennarBlueTints: {
        cobaltTint: string
        lightBlue1: string
        lightBlue2: string
        lightBlue3: string
      }
      landGreens: {
        lightLandGreen4: string
        landGreen: string
        landDarkGreen: string
      }
      landGreensTints: {
        lightLandGreen1: string
        lightLandGreen2: string
        lightLandGreen3: string
      }
      blurple: {
        lightBlurple4: string
        blurple: string
        darkBlurple: string
      }
      blurpleTints: {
        lightBlurple1: string
        lightBlurple2: string
        lightBlurple3: string
      }
      neutrals: {
        white: string
        grey1: string
        grey2: string
        grey3: string
        grey4: string
        grey5: string
        grey6: string
        grey7: string
        grey8: string
        grey9: string
        grey10: string
        grey11: string
        black: string
      }
      type: {
        title: string
        primaryText: string
        secondText: string
        tertiaryText: string
        placeholder: string
        border: string
        divider: {
          grey4: string
          grey3: string
        }
        tableHeader: {
          grey3: string
          grey2: string
          grey1: string
        }
        backgrounds: {
          grey3: string
          grey2: string
          grey1: string
        }
      }
      messaging: {
        success: {
          dark: string
          light: string
        }
        warning: {
          dark: string
          light: string
        }
        error: {
          dark: string
          light: string
        }
        progress: {
          dark: string
          light: string
        }
        default: {
          dark: string
          light: string
        }
      }
      data: {
        qualitative: {
          dataviz1: string
          dataviz2: string
          dataviz3: string
          dataviz4: string
          dataviz5: string
          dataviz6: string
          dataviz7: string
          dataviz8: string
          dataviz9: string
          dataviz10: string
          dataviz11: string
          dataviz12: string
        }
        sequential: {
          blue: {
            dataviz1: string
            dataviz2: string
            dataviz3: string
            dataviz4: string
            dataviz5: string
            dataviz6: string
          }
          green: {
            dataviz1: string
            dataviz2: string
            dataviz3: string
            dataviz4: string
            dataviz5: string
            dataviz6: string
          }
          red: {
            dataviz1: string
            dataviz2: string
            dataviz3: string
            dataviz4: string
            dataviz5: string
            dataviz6: string
          }
          purple: {
            dataviz1: string
            dataviz2: string
            dataviz3: string
            dataviz4: string
            dataviz5: string
            dataviz6: string
          }
        }
        diverging: {
          diverging1: string
          diverging2: string
          diverging3: string
          diverging4: string
          diverging5: string
          diverging6: string
          diverging7: string
          diverging8: string
          diverging9: string
        }
      }
      numeric: {
        gain: string
        loss: string
      }
      map: {
        predefined: {
          homesites: string
          streets: string
          grass: string
          trails: string
          golfCourses: string
          water: string
          mountains1: string
          mountains2: string
          mountains3: string
          sandOrShrubs1: string
          sandOrShrubs2: string
          sandOrShurbs3: string
          legendOrBorder1: string
          legendOrBorder2: string
        }
      }
      misc: {
        approvedRegionalPlanChip: string
        shellPlanChip: string
        unitPlanChip: string
      }
    }
  }
  // allow configuration using `createTheme`
  interface ThemeOptions {
    lennarDesignSystem?: {
      lennarBlues?: {
        lightBlue4?: string
        lennarBlue?: string
        oceanBlue?: string
        darkBlue?: string
      }
      lennarBlueTints?: {
        cobaltTint?: string
        lightBlue1?: string
        lightBlue2?: string
        lightBlue3?: string
      }
      landGreens?: {
        lightLandGreen4?: string
        landGreen?: string
        landDarkGreen?: string
      }
      landGreensTints?: {
        lightLandGreen1?: string
        lightLandGreen2?: string
        lightLandGreen3?: string
      }
      blurple?: {
        lightBlurple4?: string
        blurple?: string
        darkBlurple?: string
      }
      blurpleTints?: {
        lightBlurple1?: string
        lightBlurple2?: string
        lightBlurple3?: string
      }
      neutrals?: {
        white?: string
        grey1?: string
        grey2?: string
        grey3?: string
        grey4?: string
        grey5?: string
        grey6?: string
        grey7?: string
        grey8?: string
        grey9?: string
        grey10?: string
        grey11?: string
        black?: string
      }
      type?: {
        title?: string
        primaryText?: string
        secondText?: string
        tertiaryText?: string
        placeholder?: string
        border?: string
        divider?: {
          grey4: string
          grey3: string
        }
        tableHeader?: {
          grey3?: string
          grey2?: string
          grey1?: string
        }
        backgrounds?: {
          grey3?: string
          grey2?: string
          grey1?: string
        }
      }
      messaging?: {
        success: {
          dark?: string
          light?: string
        }
        warning?: {
          dark?: string
          light?: string
        }
        error?: {
          dark?: string
          light?: string
        }
        progress?: {
          dark?: string
          light?: string
        }
        default: {
          dark?: string
          light?: string
        }
      }
      data?: {
        qualitative?: {
          dataviz1?: string
          dataviz2?: string
          dataviz3?: string
          dataviz4?: string
          dataviz5?: string
          dataviz6?: string
          dataviz7?: string
          dataviz8?: string
          dataviz9?: string
          dataviz10?: string
          dataviz11?: string
          dataviz12?: string
        }
        sequential?: {
          blue?: {
            dataviz1?: string
            dataviz2?: string
            dataviz3?: string
            dataviz4?: string
            dataviz5?: string
            dataviz6?: string
          }
          green?: {
            dataviz1?: string
            dataviz2?: string
            dataviz3?: string
            dataviz4?: string
            dataviz5?: string
            dataviz6?: string
          }
          red?: {
            dataviz1?: string
            dataviz2?: string
            dataviz3?: string
            dataviz4?: string
            dataviz5?: string
            dataviz6?: string
          }
          purple?: {
            dataviz1?: string
            dataviz2?: string
            dataviz3?: string
            dataviz4?: string
            dataviz5?: string
            dataviz6?: string
          }
        }
        diverging?: {
          diverging1?: string
          diverging2?: string
          diverging3?: string
          diverging4?: string
          diverging5?: string
          diverging6?: string
          diverging7?: string
          diverging8?: string
          diverging9?: string
        }
      }
      numeric?: {
        gain?: string
        loss?: string
      }
      map?: {
        predefined?: {
          homesites?: string
          streets?: string
          grass?: string
          trails?: string
          golfCourses?: string
          water?: string
          mountains1?: string
          mountains2?: string
          mountains3?: string
          sandOrShrubs1?: string
          sandOrShrubs2?: string
          sandOrShrubs3?: string
          legendOrBorder1?: string
          legendOrBorder2?: string
        }
      }
      misc: {
        approvedRegionalPlanChip: string
        shellPlanChip: string
        unitPlanChip: string
      }
    }
  }
}

const white = '#ffffff'
const grey1 = '#fafbfc'
const grey2 = '#f4f6f9'
const grey3 = '#e7ecf0'
const grey4 = '#dge3e8'
const grey5 = '#bcc5cc'
const grey6 = '#9fb1bd'
const grey7 = '#5b7282'
const grey8 = '#3e5463'
const grey9 = '#2a3f4d'
const grey10 = '#1c2b36'
const grey11 = '#abb5bb'
const black = '#000000'

export const lennarTheme = createTheme({
  lennarDesignSystem: {
    blurple: {
      blurple: '#3946b1',
      darkBlurple: '#07117c',
      lightBlurple4: '#7883e2',
    },
    blurpleTints: {
      lightBlurple1: '#f4f4fc',
      lightBlurple2: '#e2e2f6',
      lightBlurple3: '#bdbde6',
    },
    data: {
      diverging: {
        diverging1: '#2D3974',
        diverging2: '#496B8F',
        diverging3: '#6C9EA6',
        diverging4: '#9CD3B3',
        diverging5: '#FDFF9E',
        diverging6: '#F5C083',
        diverging7: '#E47F5B',
        diverging8: '#C04441',
        diverging9: '#86193B',
      },
      qualitative: {
        dataviz1: '#0D4EA6',
        dataviz2: '#EDC707',
        dataviz3: '#993E60',
        dataviz4: '#9BBA75',
        dataviz5: '#334173',
        dataviz6: '#48B8F0',
        dataviz7: '#4C277D',
        dataviz8: '#F08069',
        dataviz9: '#165C60',
        dataviz10: '#F2B6DE',
        dataviz11: '#BF4815',
        dataviz12: '#86D9D4',
      },
      sequential: {
        blue: {
          dataviz1: '#EFF3FF',
          dataviz2: '#C6DBEF',
          dataviz3: '#9ECAE1',
          dataviz4: '#6BAED6',
          dataviz5: '#3182BD',
          dataviz6: '#08519C',
        },
        green: {
          dataviz1: '#EDF8E9',
          dataviz2: '#C7E9C0',
          dataviz3: '#A1D99B',
          dataviz4: '#74C476',
          dataviz5: '#31A354',
          dataviz6: '#006D2C',
        },
        purple: {
          dataviz1: '#F2F0F7',
          dataviz2: '#DADAEB',
          dataviz3: '#BCBDDC',
          dataviz4: '#9E9AC8',
          dataviz5: '#756BB1',
          dataviz6: '#54278F',
        },
        red: {
          dataviz1: '#FFFFB2',
          dataviz2: '#FFFFB2',
          dataviz3: '#FEB24C',
          dataviz4: '#FD8D3C',
          dataviz5: '#F03B20',
          dataviz6: '#BD0026',
        },
      },
    },
    landGreens: {
      landDarkGreen: '#074a38',
      landGreen: '#1e6d58',
      lightLandGreen4: '#288666',
    },
    landGreensTints: {
      lightLandGreen1: '#eef5f3',
      lightLandGreen2: '#dfede8',
      lightLandGreen3: '#bcd3d0',
    },
    lennarBlues: {
      darkBlue: '#003c77',
      lennarBlue: '#005daa',
      lightBlue4: '#1f75bb',
      // Ocean Blue: the brand's hover partner for Cobalt Blue (lennarBlue).
      oceanBlue: '#203f7c',
    },
    lennarBlueTints: {
      // Cobalt Blue at 10%: hover and active background for cobalt-on-white controls.
      cobaltTint: '#edf3f9',
      lightBlue1: '#f3faff',
      lightBlue2: '#e0f1fe',
      lightBlue3: '#c4e3fe',
    },
    map: {
      predefined: {
        golfCourses: '#CED8D0',
        grass: '#EEF2ED',
        homesites: '#FAFBFB',
        legendOrBorder1: '#5B7282',
        legendOrBorder2: '#9FB1BD',
        mountains1: '#C8D7B9',
        mountains2: '#C2E2C7',
        mountains3: '#D8ECDC',
        sandOrShrubs1: '#F2ECE8',
        sandOrShrubs2: '#F7F4F1',
        sandOrShrubs3: '#FDFBF8',
        streets: '#F4F4F4',
        trails: '#E4E7E4',
        water: '#BDDBF2',
      },
    },
    messaging: {
      default: {
        dark: '#5b7282',
        light: '#f4f6f9',
      },
      error: {
        dark: '#c93939',
        light: '#ffeff1',
      },
      progress: {
        dark: '#3f63c0',
        light: '#edf4ff',
      },
      success: {
        dark: '#11834a',
        light: '#effbf5',
      },
      warning: {
        dark: '#ffc107',
        light: '#fff8e1',
      },
    },
    misc: {
      // Distinct from the orange shell and green unit chips and from the amber High
      // Value Plan chip, at a similar lightness so the dark chip text stays readable.
      approvedRegionalPlanChip: '#75AACC',
      shellPlanChip: '#FC8452',
      unitPlanChip: '#91CC75',
    },
    neutrals: {
      black,
      grey1,
      grey2,
      grey3,
      grey4,
      grey5,
      grey6,
      grey7,
      grey8,
      grey9,
      grey10,
      white,
    },
    numeric: {
      gain: '#11834A',
      loss: '#C93939',
    },
    type: {
      backgrounds: {
        grey1,
        grey2,
        grey3,
      },
      border: grey4,
      divider: {
        grey3,
        grey4,
      },
      placeholder: grey11,
      primaryText: grey10,
      secondText: grey8,
      tableHeader: {
        grey1,
        grey2,
        grey3,
      },
      tertiaryText: grey7,
      title: grey10,
    },
  },
  palette: {
    mode: 'light',
    primary: {
      main: '#003C77',
    },
    secondary: {
      main: '#005DAA',
    },
  },
  typography: {
    fontFamily: ['karla', 'Roboto', 'Arial'].join(','),
  },
})
